import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, IconButton, MenuItem, Paper, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { BankAccountService } from '../../services/bankAccount.service';
import { BankRuleService } from '../../services/bankRule.service';
import { AccountService } from '../../services/account.service';
import { PERMISSIONS } from '../../constants/permissions';
import { Account } from '../../interfaces/accounting.interface';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import { ACTION_LABEL, BankRule, BankRuleCondition, BankRuleHitStat, OPERATOR_LABEL } from '../../interfaces/bank/bankRule.interface';
import { SearchableSelect } from '../common/SearchableSelect';
import { formatDate } from '../../utils/dateUtils';
import BankRuleEditorDialog from './BankRuleEditorDialog';

const describeCondition = (c: BankRuleCondition): string => {
  const op = OPERATOR_LABEL[c.operator];
  if (c.valueNumber != null) {
    return c.operator === 'BETWEEN' ? `${c.field} ${op} ${c.valueNumber} and ${c.valueNumberTo}` : `${c.field} ${op} ${c.valueNumber}`;
  }
  return `${c.field} ${op} “${c.valueText ?? ''}”`;
};

/** Banking > Bank Rules: the ordered list of rules that suggest how statement lines should be posted. */
const BankRules: React.FC = () => {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.BANK_RULE_MANAGE);
  const institutionId = AuthService.getSelectedInstitution()!;
  const [rules, setRules] = useState<BankRule[]>([]);
  const [banks, setBanks] = useState<BankAccount[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [bankFilter, setBankFilter] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [hitStats, setHitStats] = useState<BankRuleHitStat[]>([]);   // FW-4B
  const [editor, setEditor] = useState<{ rule: BankRule | null } | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRules(await BankRuleService.list(institutionId, {
        bankAccountId: bankFilter || undefined,
        active: status === '' ? undefined : status === 'ACTIVE',
        search: search.trim() || undefined,
      }));
    } catch (e) {
      toast.showApiError(e, 'Failed to load bank rules');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId, bankFilter, status, search]);

  useEffect(() => { load(); }, [load]);
  // ---- FW-4B: monthly hit counts per rule (best effort) ----
  useEffect(() => {
    Promise.resolve(BankRuleService.hitStats(institutionId, 6)).then(h => setHitStats(h ?? [])).catch(() => setHitStats([]));
  }, [institutionId, rules.length]);
  const monthsOf = (ruleId: string) => hitStats.filter(h => h.ruleId === ruleId).sort((a, b) => b.month.localeCompare(a.month));
  // ---- end FW-4B ----
  useEffect(() => {
    BankAccountService.listByInstitution(institutionId).then(setBanks).catch(() => setBanks([]));
    AccountService.getAllAccounts().then((a: Account[]) => setAccounts(Array.isArray(a) ? a : [])).catch(() => setAccounts([]));
  }, [institutionId]);

  const bankName = (id?: string | null) => {
    const b = banks.find(x => x.id === id);
    return b ? `${b.bankName} — ${b.accountNumber}` : 'All bank accounts';
  };
  const accountLabel = (id?: string | null) => {
    const a = accounts.find(x => x.id === id);
    return a ? `${a.code} - ${a.name}` : '—';
  };

  // Reordering only makes sense on the unfiltered list (it renumbers every rule).
  const unfiltered = !bankFilter && !status && !search.trim();
  const move = async (index: number, delta: number) => {
    const next = [...rules];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    try {
      setRules(await BankRuleService.reorder(institutionId, next.map(r => r.id)));
    } catch (e) {
      toast.showApiError(e, 'Failed to reorder rules');
    }
  };

  const toggle = async (rule: BankRule) => {
    try {
      await BankRuleService.setActive(institutionId, rule.id, !rule.active);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to change the rule status');
    }
  };

  const remove = async (rule: BankRule) => {
    if (!window.confirm(`Delete rule “${rule.name}”?`)) return;
    try {
      await BankRuleService.remove(institutionId, rule.id);
      toast.showSuccess('Rule deleted');
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to delete the rule');
    }
  };

  const bankOptions = useMemo(() => banks, [banks]);

  return (
    <Box data-help-page="banking.rules.page" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5">Bank Rules</Typography>
          <Typography variant="body2" color="text.secondary">
            Rules are tried top to bottom; the first match wins. They only suggest: lines post when you apply them from a reconciliation.
          </Typography>
        </Box>
        {canManage && (
          <Button data-help="banking.rules.new" variant="contained" startIcon={<AddIcon />} onClick={() => setEditor({ rule: null })}>New rule</Button>
        )}
      </Box>

      <Box data-help-section="banking.rules.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box data-help="banking.rules.bankFilter" sx={{ flex: '1 1 260px', maxWidth: 360 }}>
          <SearchableSelect<BankAccount>
            options={bankOptions} value={bankFilter} onChange={id => setBankFilter(id)} size="small"
            getOptionLabel={b => `${b.bankName} — ${b.accountNumber}`} label="Bank account" placeholder="All bank accounts"
            data-testid="rules-bank-filter" />
        </Box>
        <TextField data-help="banking.rules.statusFilter" select size="small" label="Status" value={status} onChange={e => setStatus(e.target.value)}
          sx={{ flex: '1 1 160px', maxWidth: 220 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="ACTIVE">Active</MenuItem>
          <MenuItem value="INACTIVE">Inactive</MenuItem>
        </TextField>
        <TextField data-help="banking.rules.search" size="small" label="Search" value={search} onChange={e => setSearch(e.target.value)}
          sx={{ flex: '1 1 220px', maxWidth: 320 }} />
      </Box>

      {loading ? <Box sx={{ p: 3 }}><CircularProgress /></Box> : (
        <Paper data-help-section="banking.rules.table" variant="outlined">
          <TableContainer>
            <Table size="small" data-testid="bank-rules-table">
              <TableHead>
                <TableRow>
                  <TableCell data-help="banking.rules.col.order">#</TableCell>
                  <TableCell data-help="banking.rules.col.rule">Rule</TableCell>
                  <TableCell data-help="banking.rules.col.bank">Bank account</TableCell>
                  <TableCell data-help="banking.rules.col.when">When</TableCell>
                  <TableCell data-help="banking.rules.col.action">Action</TableCell>
                  <TableCell data-help="banking.rules.col.applied" align="right">Applied (all time)</TableCell>
                  <TableCell data-help="banking.rules.col.lastApplied">Last applied</TableCell>
                  <TableCell data-help="banking.rules.col.active">Active</TableCell>
                  {canManage && <TableCell data-help="banking.rules.col.actions" align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((r, i) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                    <TableCell sx={{ whiteSpace: 'normal' }}>{bankName(r.scopeId)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'normal', minWidth: 220 }}>
                      {r.conditions.map((c, idx) => <Chip key={idx} size="small" variant="outlined" label={describeCondition(c)} sx={{ mr: 0.5, mb: 0.5 }} />)}
                      {r.conditions.length > 1 && <Typography variant="caption" color="text.secondary">{r.matchMode === 'ALL' ? 'all must match' : r.matchMode === 'ANY' ? 'any may match' : 'none may match'}</Typography>}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'normal' }}>
                      {r.action ? (ACTION_LABEL[r.action.actionType] ?? r.action.actionType) : '—'}
                      {r.action?.accountId ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{accountLabel(r.action.accountId)}</Typography> : null}
                    </TableCell>
                    <TableCell align="right">
                      {r.hitCount}
                      {monthsOf(r.id).map(m => (
                        <Typography key={m.month} variant="caption" color="text.secondary" sx={{ display: 'block' }} data-testid={`rule-hits-${r.id}-${m.month}`}>
                          {m.month}: {m.hits} still applied
                        </Typography>
                      ))}
                    </TableCell>
                    <TableCell>{r.lastHitAt ? formatDate(r.lastHitAt) : '—'}</TableCell>
                    <TableCell data-help="banking.rules.action.toggle">
                      <Switch size="small" checked={r.active} disabled={!canManage} onChange={() => toggle(r)}
                        inputProps={{ 'aria-label': `${r.active ? 'Deactivate' : 'Activate'} ${r.name}` }} />
                    </TableCell>
                    {canManage && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={unfiltered ? 'Move up' : 'Clear the filters to reorder'}>
                          <span><IconButton data-help="banking.rules.action.moveUp" size="small" disabled={!unfiltered || i === 0} onClick={() => move(i, -1)} aria-label={`Move ${r.name} up`}><ArrowUpwardIcon fontSize="small" /></IconButton></span>
                        </Tooltip>
                        <Tooltip title={unfiltered ? 'Move down' : 'Clear the filters to reorder'}>
                          <span><IconButton data-help="banking.rules.action.moveDown" size="small" disabled={!unfiltered || i === rules.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${r.name} down`}><ArrowDownwardIcon fontSize="small" /></IconButton></span>
                        </Tooltip>
                        <IconButton data-help="banking.rules.action.edit" size="small" onClick={() => setEditor({ rule: r })} aria-label={`Edit ${r.name}`}><EditIcon fontSize="small" /></IconButton>
                        <IconButton data-help="banking.rules.action.delete" size="small" color="error" onClick={() => remove(r)} aria-label={`Delete ${r.name}`}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {rules.length === 0 && (
                  <TableRow><TableCell colSpan={canManage ? 9 : 8} align="center">No bank rules yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {editor && (
        <BankRuleEditorDialog
          rule={editor.rule}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); }}
        />
      )}
    </Box>
  );
};

export default BankRules;
